import Phaser from 'phaser';
import type { BiomeId } from '@/domain/world/types';
import { regionBounds } from '@/domain/world/region';
import { ALL_BIOMES } from '@/domain/world/types';
import { useStore } from '@/infra/store';

/**
 * Particles ambientais por bioma para dar vida ao mundo:
 * - meadow: borboletas voando
 * - forest: vagalumes piscando
 * - cave: motes brilhantes flutuando
 * - coast: gaivotas (pequenos pontos brancos voando)
 */

export interface AmbientHandle {
  destroy(): void;
}

export function buildAmbient(scene: Phaser.Scene): AmbientHandle {
  const sprites: Phaser.GameObjects.GameObject[] = [];
  const tweens: Phaser.Tweens.Tween[] = [];

  // Respeita reduced motion: sem partículas animadas
  const reducedMotion = useStore.getState().settings.accessibility.reducedMotion;
  if (reducedMotion) {
    return { destroy(): void { /* nada */ } };
  }

  for (const biome of ALL_BIOMES) {
    spawnFor(scene, biome, sprites, tweens);
  }

  return {
    destroy(): void {
      tweens.forEach((t) => t.stop());
      sprites.forEach((s) => s.destroy());
    },
  };
}

function spawnFor(
  scene: Phaser.Scene,
  biome: BiomeId,
  sprites: Phaser.GameObjects.GameObject[],
  tweens: Phaser.Tweens.Tween[],
): void {
  const b = regionBounds(biome);
  switch (biome) {
    case 'meadow':
      for (let i = 0; i < 6; i++) spawnButterfly(scene, b, sprites, tweens);
      break;
    case 'forest':
      for (let i = 0; i < 8; i++) spawnFirefly(scene, b, sprites, tweens);
      break;
    case 'cave':
      for (let i = 0; i < 6; i++) spawnSparkle(scene, b, sprites, tweens);
      break;
    case 'coast':
      for (let i = 0; i < 4; i++) spawnSeagull(scene, b, sprites, tweens);
      break;
  }
}

function spawnButterfly(
  scene: Phaser.Scene,
  b: { x: number; y: number; w: number; h: number },
  sprites: Phaser.GameObjects.GameObject[],
  tweens: Phaser.Tweens.Tween[],
): void {
  const x = b.x + 50 + Math.random() * (b.w - 100);
  const y = b.y + 50 + Math.random() * (b.h - 100);
  const colors = [0xffd84a, 0xff8db1, 0xa8d8ff, 0xb388ff];
  const color = colors[Math.floor(Math.random() * colors.length)]!;
  const g = scene.add.graphics();
  g.fillStyle(color, 1);
  g.fillRect(-2, -1, 5, 1);
  g.fillRect(-1, -2, 3, 1);
  g.fillStyle(0x1a1a14, 1);
  g.fillRect(0, -1, 1, 2);
  g.setPosition(x, y);
  g.setDepth(15);
  sprites.push(g);

  // Tween: voo errático
  const travel = (): void => {
    const dx = (Math.random() - 0.5) * 200;
    const dy = (Math.random() - 0.5) * 100;
    const t = scene.tweens.add({
      targets: g,
      x: g.x + dx,
      y: g.y + dy,
      duration: 2000 + Math.random() * 2000,
      ease: 'Sine.InOut',
      onComplete: travel,
    });
    tweens.push(t);
  };
  travel();
  // wing flap
  const flap = scene.tweens.add({
    targets: g,
    scaleX: 0.7,
    duration: 100,
    yoyo: true,
    repeat: -1,
  });
  tweens.push(flap);
}

function spawnFirefly(
  scene: Phaser.Scene,
  b: { x: number; y: number; w: number; h: number },
  sprites: Phaser.GameObjects.GameObject[],
  tweens: Phaser.Tweens.Tween[],
): void {
  const x = b.x + 30 + Math.random() * (b.w - 60);
  const y = b.y + 30 + Math.random() * (b.h - 60);
  const g = scene.add.circle(x, y, 2, 0xffd84a, 0.9);
  g.setDepth(14);
  sprites.push(g);
  const flick = scene.tweens.add({
    targets: g,
    alpha: 0.2,
    duration: 600 + Math.random() * 400,
    yoyo: true,
    repeat: -1,
  });
  tweens.push(flick);
  const drift = scene.tweens.add({
    targets: g,
    y: y - 8,
    x: x + (Math.random() - 0.5) * 16,
    duration: 4000,
    yoyo: true,
    repeat: -1,
    ease: 'Sine.InOut',
  });
  tweens.push(drift);
}

function spawnSparkle(
  scene: Phaser.Scene,
  b: { x: number; y: number; w: number; h: number },
  sprites: Phaser.GameObjects.GameObject[],
  tweens: Phaser.Tweens.Tween[],
): void {
  const x = b.x + 30 + Math.random() * (b.w - 60);
  const y = b.y + 30 + Math.random() * (b.h - 60);
  const g = scene.add.graphics();
  g.fillStyle(0xb0eaff, 1);
  g.fillRect(0, 0, 1, 1);
  g.fillRect(-1, 0, 1, 1);
  g.fillRect(1, 0, 1, 1);
  g.fillRect(0, -1, 1, 1);
  g.fillRect(0, 1, 1, 1);
  g.setPosition(x, y);
  g.setDepth(14);
  sprites.push(g);
  const fade = scene.tweens.add({
    targets: g,
    alpha: 0.1,
    duration: 800,
    yoyo: true,
    repeat: -1,
    ease: 'Sine.InOut',
  });
  tweens.push(fade);
}

function spawnSeagull(
  scene: Phaser.Scene,
  b: { x: number; y: number; w: number; h: number },
  sprites: Phaser.GameObjects.GameObject[],
  tweens: Phaser.Tweens.Tween[],
): void {
  const startX = b.x - 20;
  const y = b.y + 30 + Math.random() * 200;
  const g = scene.add.graphics();
  g.fillStyle(0xffffff, 1);
  // forma de "M" tipo gaivota distante
  g.fillRect(-3, 0, 2, 1);
  g.fillRect(-1, -1, 1, 1);
  g.fillRect(0, 0, 1, 1);
  g.fillRect(1, -1, 1, 1);
  g.fillRect(2, 0, 2, 1);
  g.setPosition(startX, y);
  g.setDepth(15);
  sprites.push(g);
  const fly = scene.tweens.add({
    targets: g,
    x: b.x + b.w + 20,
    y: y + (Math.random() - 0.5) * 60,
    duration: 12000 + Math.random() * 4000,
    repeat: -1,
    onRepeat: () => {
      g.x = startX;
      g.y = y;
    },
  });
  tweens.push(fly);
}
